import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, Drawer, Form, Input, Popconfirm, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { get, patch, post } from '../../lib/request'
import { MERCHANT_STATUS_LABELS, Merchant, errMsg, fmtTime } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

/**
 * 商户档案（thankyou_v2 批次 8.2 新建页，需求 4.4「每商家一套券」的档案侧）。
 *
 * 样板：pages/Blacklist（表格 + 弹层 CRUD）+ pages/Referrers（抽屉编辑）。
 * 后端契约：/admin/merchants CRUD（仅软删，无物理 DELETE 语义），写操作挂 MERCHANT_MANAGE。
 * 推荐人档案的「关联商户」下拉消费本页维护的记录（决定券出自哪家店）。
 */
interface MerchantFormValue {
  name: string
  nameEn?: string
  contactName?: string
  contactPhone?: string
  note?: string
  status: string
}

const STATUS_OPTIONS = [
  { value: 'active', label: '在营' },
  { value: 'inactive', label: '已停用' },
]

const MerchantsPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  // 页面入口由 RoleRoute（super_admin/admin）守卫；写操作再按 MERCHANT_MANAGE 收敛按钮
  const canEdit = hasPermission(role, 'MERCHANT_MANAGE')

  const [list, setList] = useState<Merchant[]>([])
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [editing, setEditing] = useState<Merchant | 'new' | null>(null)
  const [form] = Form.useForm<MerchantFormValue>()
  const [saving, setSaving] = useState(false)

  const fetchList = useCallback(() => {
    setLoading(true)
    get<Merchant[] | { items: Merchant[] }>('/admin/merchants', {
      params: keyword ? { keyword } : {},
    })
      .then((d) => setList(Array.isArray(d) ? d : d.items ?? []))
      .catch((err) => {
        message.warning(errMsg(err, '商户档案加载失败（后端可能未启动）'))
        setList([])
      })
      .finally(() => setLoading(false))
  }, [keyword])

  useEffect(() => { fetchList() }, [fetchList])

  const openEdit = (r?: Merchant) => {
    if (r) {
      setEditing(r)
      form.setFieldsValue({
        name: r.name,
        nameEn: r.nameEn ?? undefined,
        contactName: r.contactName ?? undefined,
        contactPhone: r.contactPhone ?? undefined,
        note: r.note ?? undefined,
        status: r.status,
      })
    } else {
      setEditing('new')
      form.resetFields()
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    const payload = {
      name: v.name.trim(),
      nameEn: v.nameEn?.trim() || null,
      contactName: v.contactName?.trim() || null,
      contactPhone: v.contactPhone?.trim() || null,
      note: v.note?.trim() || null,
      status: v.status,
    }
    setSaving(true)
    try {
      if (editing === 'new') {
        await post('/admin/merchants', payload)
        message.success('商户档案已新建')
      } else if (editing) {
        await patch(`/admin/merchants/${editing.id}`, payload)
        message.success('商户档案已更新')
      }
      setEditing(null)
      fetchList()
    } catch (err: unknown) {
      // 后端文案原样透出（名称重复 / 参数无效 / 403 无 MERCHANT_MANAGE）
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  /** 停用 = 软删（契约：/admin/merchants 仅软删） */
  const handleOffline = (r: Merchant) => {
    patch(`/admin/merchants/${r.id}`, { status: 'inactive' })
      .then(() => { message.success('商户已停用（软删）'); fetchList() })
      .catch((err: unknown) => message.error(errMsg(err, '停用失败')))
  }

  const columns: ColumnsType<Merchant> = [
    { title: '名称', dataIndex: 'name', key: 'name', render: (v: string) => <Text strong>{v}</Text> },
    {
      title: '英文名',
      dataIndex: 'nameEn',
      key: 'nameEn',
      render: (v?: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      render: (v?: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: '电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      render: (v?: string | null) => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => (
        <Tag color={MERCHANT_STATUS_LABELS[v]?.color ?? 'default'}>
          {MERCHANT_STATUS_LABELS[v]?.label ?? v}
        </Tag>
      ),
    },
    { title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 160, render: fmtTime },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          fixed: 'right' as const,
          width: 170,
          render: (_: unknown, r: Merchant) => (
            <Space size={4} wrap>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>编辑</Button>
              <Popconfirm
                title="停用该商户？"
                description="软删：不再出现在推荐人「关联商户」下拉；历史绑定关系保留"
                okText="停用"
                cancelText="取消"
                disabled={r.status === 'inactive'}
                onConfirm={() => handleOffline(r)}
              >
                <Button
                  size="small"
                  danger
                  style={{ minHeight: 32 }}
                  disabled={r.status === 'inactive'}
                >
                  停用
                </Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <>
      <Space style={{ marginBottom: 14 }} wrap>
        <Input.Search
          allowClear
          placeholder="搜索商户名称 / 联系人 / 电话"
          style={{ width: 280 }}
          onSearch={(v) => setKeyword(v)}
        />
        {canEdit && (
          <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
            + 新建商户
          </Button>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          商户档案仅软删；新增商户不产生新方案——券面值由方案分档子表按成交面积统一套档（需求 4.4）。
        </Text>
      </Space>
      <Table<Merchant>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />

      <Drawer
        title={editing === 'new' ? '新建商户档案' : `编辑商户档案 · ${editing?.name ?? ''}`}
        open={!!editing}
        onClose={() => setEditing(null)}
        width={Math.min(480, window.innerWidth)}
        extra={
          <Button type="primary" loading={saving} onClick={save} style={{ minHeight: 44 }}>
            保存
          </Button>
        }
        destroyOnClose
      >
        <Form<MerchantFormValue> form={form} layout="vertical" initialValues={{ status: 'active' }}>
          <Form.Item
            name="name"
            label="商户名称"
            rules={[{ required: true, message: '商户名称必填' }, { max: 100, message: '不超过 100 字' }]}
          >
            <Input maxLength={100} placeholder="例：XX 餐饮管理有限公司" />
          </Form.Item>
          <Form.Item name="nameEn" label="英文名（选填）" rules={[{ max: 100, message: '不超过 100 字' }]}>
            <Input maxLength={100} placeholder="e.g. XX Catering Co., Ltd." />
          </Form.Item>
          <Form.Item name="contactName" label="联系人（选填）" rules={[{ max: 32, message: '不超过 32 字' }]}>
            <Input maxLength={32} placeholder="例：李经理" />
          </Form.Item>
          <Form.Item
            name="contactPhone"
            label="联系电话（选填）"
            rules={[{ pattern: /^[0-9+\-\s()]{6,20}$/, message: '请输入有效电话（6-20 位数字）' }]}
          >
            <Input maxLength={20} placeholder="例：19142530351" />
          </Form.Item>
          <Form.Item
            name="note"
            label="备注（选填：门店地址 / 券规格 / 协议要点）"
            rules={[{ max: 500, message: '不超过 500 字' }]}
          >
            <Input.TextArea rows={3} placeholder="门店地址、券形式与有效期、采购结算节奏等" maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            仅持有 MERCHANT_MANAGE（超管 / 管理员）者可保存；停用为软删，历史绑定不受影响。
          </Text>
        </Form>
      </Drawer>
    </>
  )
}

export default MerchantsPage
